import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronsUpDown,
  LifeBuoy,
  LogOut,
  Maximize2,
  Minimize2,
  Moon,
  Settings,
  UserCircle,
  type LucideIcon,
} from 'lucide-react';
import { useSession } from '@/features/auth';
import { cn } from '@/lib/utils';
import { Avatar } from '@/shared/ui/Avatar';
import { Button } from '@/shared/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { useFullscreen } from '@/shared/ui/use-fullscreen';

const menuItemClass =
  'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[13px] transition';

interface UserMenuItemProps {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  to?: string;
  trailing?: ReactNode;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

function UserMenuItem({
  icon: Icon,
  label,
  onClick,
  to,
  trailing,
  variant = 'default',
  disabled = false,
}: UserMenuItemProps): JSX.Element {
  const className = cn(
    menuItemClass,
    variant === 'danger'
      ? 'font-semibold text-coral-600 hover:bg-coral-50'
      : disabled
        ? 'cursor-not-allowed text-ink-400'
        : 'text-ink-700 hover:bg-surface-100',
  );

  const content = (
    <>
      <Icon
        className={cn(
          'size-4 shrink-0',
          variant === 'danger' ? 'text-coral-600' : disabled ? 'text-ink-400' : 'text-ink-500',
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1">{label}</span>
      {trailing ? <span className="flex shrink-0 items-center justify-center">{trailing}</span> : null}
    </>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className={className} onClick={onClick} role="menuitem">
        {content}
      </Link>
    );
  }

  return (
    <Button
      variant="ghost"
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      role="menuitem"
      aria-disabled={disabled || undefined}
      title={disabled ? 'Coming soon' : undefined}
    >
      {content}
    </Button>
  );
}

export interface UserMenuProps {
  /** Sidebar footer uses dark trigger + upward popover. */
  variant?: 'sidebar' | 'header';
  isCollapsed?: boolean;
}

/**
 * Account menu — sidebar footer (default) or compact header trigger.
 */
export function UserMenu({ variant = 'sidebar', isCollapsed = false }: UserMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const { user, logout } = useSession();
  const { isFullscreen, toggleFullscreen } = useFullscreen();

  const name = user?.name?.trim() || 'User';
  const email = user?.email?.trim() || 'No email';
  const role = user?.role
    ? user.role
        .split('_')
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
        .join(' ')
    : 'Unknown role';

  const isSidebar = variant === 'sidebar';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Account menu"
          title={isCollapsed ? name : undefined}
          className={cn(
            'h-auto w-full justify-start gap-2.5 rounded-xl px-2 py-2',
            isSidebar
              ? 'text-ink-300 hover:bg-white/5 hover:text-white'
              : 'rounded-xl py-1 pl-1 pr-2 text-ink-900',
            isCollapsed && isSidebar && 'justify-center px-0',
          )}
        >
          <Avatar
            name={name}
            size="sm"
            className={cn('ring-2', isSidebar ? 'ring-white/10' : 'ring-white')}
          />
          {(!isCollapsed || !isSidebar) && (
            <>
              <div className={cn('min-w-0 flex-1 text-left leading-tight', isSidebar ? '' : 'hidden md:block')}>
                <div
                  className={cn(
                    'truncate text-[13px] font-semibold',
                    isSidebar ? 'text-white' : 'text-ink-900',
                  )}
                >
                  {name}
                </div>
                <div
                  className={cn(
                    'max-w-[160px] truncate text-[11px]',
                    isSidebar ? 'text-ink-500' : 'text-ink-500',
                  )}
                >
                  {role}
                </div>
              </div>
              <ChevronsUpDown
                className={cn(
                  'size-4 shrink-0',
                  isSidebar ? 'text-ink-500' : 'hidden text-ink-500 md:block',
                )}
                aria-hidden
              />
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side={isSidebar ? 'top' : 'bottom'}
        align={isSidebar ? 'start' : 'end'}
        sideOffset={8}
        className="w-64 p-0"
        role="menu"
      >
        <div className="flex items-center gap-3 border-b hairline px-4 py-3">
          <Avatar name={name} size="md" className="ring-2 ring-white" />
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-semibold text-ink-900">{name}</div>
            <div className="truncate text-[11px] text-ink-500">{email}</div>
          </div>
        </div>

        <div className="p-1.5">
          <UserMenuItem
            icon={UserCircle}
            label="My Profile"
            to="/profile"
            onClick={() => setOpen(false)}
          />
          <UserMenuItem
            icon={Settings}
            label="Account Settings"
            to="/settings"
            onClick={() => setOpen(false)}
          />
          <UserMenuItem
            icon={Moon}
            label="Dark appearance"
            disabled
            trailing={
              <span className="pill bg-surface-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-500">
                Coming soon
              </span>
            }
          />
          <UserMenuItem
            icon={isFullscreen ? Minimize2 : Maximize2}
            label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            onClick={() => {
              toggleFullscreen();
              setOpen(false);
            }}
          />
          <UserMenuItem
            icon={LifeBuoy}
            label="Help & Support"
            to="/help-support"
            onClick={() => setOpen(false)}
          />
        </div>

        <div className="border-t hairline p-1.5">
          <UserMenuItem
            icon={LogOut}
            label="Sign Out"
            variant="danger"
            onClick={() => {
              setOpen(false);
              logout();
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
